import { sessionService } from '@/services/sessionService'
import { TpaSessionStoreState } from '@/types/states/sessions'
import { TpaSession, TpaSessionMap } from '@/types/tpaSession'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useTeamStore } from './teamStore'

export const useSessionStore = defineStore(
  'session',
  () => {
    // State
    const sessions = ref<TpaSessionMap>({})
    const currentSession = ref<TpaSession | null>(null)

    // Actions
    function setSessions(sessionArray: TpaSession[]) {
      sessions.value = sessionArray.reduce((map: TpaSessionMap, session: TpaSession) => {
        map[session.id] = session
        return map
      }, {} as TpaSessionMap)
    }

    function setCurrentSession(session: TpaSession | null) {
      // Force reactivity by creating a new reference if session exists
      currentSession.value = session ? { ...session } : null
    }

    function addSession(session: TpaSession) {
      sessions.value[session.id] = session
    }

    function updateSession(updatedSession: TpaSession) {
      sessions.value[updatedSession.id] = updatedSession
      if (currentSession.value?.id === updatedSession.id) {
        // Force reactivity by creating a new object reference
        currentSession.value = { ...updatedSession }
      }
    }

    function removeSession(sessionId: string) {
      delete sessions.value[sessionId]
      if (currentSession.value?.id === sessionId) {
        currentSession.value = null
      }
    }

    async function loadCurrentSession(this: TpaSessionStoreState): Promise<boolean> {
      const teamStore = useTeamStore()
      if (!teamStore.currentTeam) {
        return false
      }

      if (!this.currentSession || !this.currentSession) {
        return false
      }
      return true
    }

    async function loadSessionsByTeamIdFromApi(teamId: string): Promise<void> {
      try {
        const fetchedSessions = await sessionService.fetchByTeam(teamId)
        if (fetchedSessions) {
          setSessions(fetchedSessions)

          const preservedSession = currentSession.value
            ? fetchedSessions.find((session) => session.id === currentSession.value?.id)
            : undefined

          const latestSession = fetchedSessions.length
            ? fetchedSessions.reduce((a, b) => (a.phase >= b.phase ? a : b))
            : null

          const sessionToSelect = preservedSession ?? latestSession

          setCurrentSession(sessionToSelect)
        }
      } catch (error) {
        console.error('Failed to load sessions from API:', error)
      }
    }

    function clear() {
      sessions.value = {}
      currentSession.value = null
      localStorage.removeItem('currentSession')
      localStorage.removeItem('sessionStore')
    }

    return {
      sessions,
      currentSession,
      setSessions,
      setCurrentSession,
      addSession,
      updateSession,
      removeSession,
      loadCurrentSession,
      loadSessionsByTeamIdFromApi,
      clear,
    }
  },
  {
    persist: {
      key: 'sessionStore',
      storage: localStorage,
    },
  },
)
